import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import CookieBanner from '../../components/feature/CookieBanner';

const ServiciosPage = () => {
  const [activeService, setActiveService] = useState('tabiques');

  useEffect(() => {
    document.title = 'Servicios de Pladur en L\'Hospitalet | PLADUREX';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Servicios completos de pladur en L\'Hospitalet: tabiques, falsos techos, trasdosados, aislamiento acústico y térmico, pladur antihumedad y reformas integrales.');
    }

    // Detectar hash en URL
    const hash = window.location.hash.replace('#', '');
    if (hash) {
      setActiveService(hash);
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const services = [
    {
      id: 'tabiques',
      title: 'Tabiques de Pladur',
      icon: 'ri-layout-grid-line',
      description: 'Divisiones interiores versátiles y rápidas de instalar',
      image: 'https://public.readdy.ai/ai/img_res/6d1eb69727cc5eede26bac0f504b6192.jpg',
      includes: [
        'Tabiques divisorios simples o dobles',
        'Perfilería metálica certificada',
        'Placas de pladur estándar o reforzadas',
        'Aislamiento acústico opcional',
        'Acabado liso listo para pintar',
        'Paso de instalaciones (electricidad, fontanería)'
      ],
      idealFor: [
        'Crear nuevas habitaciones o despachos',
        'Dividir espacios abiertos',
        'Separar zonas en oficinas',
        'Redistribuir viviendas'
      ],
      priceFactors: 'El precio depende de los metros cuadrados, altura del tabique, si es simple o doble, tipo de aislamiento y nivel de acabado.'
    },
    {
      id: 'techos',
      title: 'Falsos Techos',
      icon: 'ri-stack-line',
      description: 'Techos continuos y registrables para mejorar acústica e integrar instalaciones',
      image: 'https://readdy.ai/api/search-image?query=white%20suspended%20ceiling%20drywall%20modern%20interior%20construction%20smooth%20finish%20integrated%20lighting%20professional&width=600&height=800&seq=service-techos-006&orientation=portrait',
      includes: [
        'Techos continuos de pladur',
        'Techos registrables desmontables',
        'Estructura de perfilería suspendida',
        'Integración de iluminación LED',
        'Aislamiento térmico y acústico',
        'Acabado liso o con juntas vistas'
      ],
      idealFor: [
        'Ocultar instalaciones (cables, tuberías)',
        'Mejorar aislamiento acústico',
        'Reducir altura de techos',
        'Integrar iluminación empotrada',
        'Renovar techos antiguos'
      ],
      priceFactors: 'El coste varía según los metros cuadrados, altura del techo, tipo (continuo o registrable), complejidad de la iluminación y acabados.'
    },
    {
      id: 'trasdosados',
      title: 'Trasdosados y Aislamiento',
      icon: 'ri-shield-line',
      description: 'Revestimiento de paredes para mejorar aislamiento térmico y acústico',
      image: 'https://readdy.ai/api/search-image?query=wall%20insulation%20drywall%20mineral%20wool%20installation%20interior%20construction%20professional%20work%20energy%20efficiency&width=600&height=800&seq=service-trasdosado-007&orientation=portrait',
      includes: [
        'Trasdosados directos sobre pared existente',
        'Trasdosados autoportantes con cámara',
        'Aislamiento térmico (lana mineral, EPS)',
        'Aislamiento acústico (lana de roca)',
        'Corrección de irregularidades',
        'Mejora de eficiencia energética'
      ],
      idealFor: [
        'Aislar paredes exteriores del frío',
        'Reducir ruido de vecinos',
        'Corregir paredes irregulares',
        'Mejorar confort térmico',
        'Reducir consumo energético'
      ],
      priceFactors: 'El precio depende de los metros cuadrados, tipo de trasdosado (directo o autoportante), grosor y tipo de aislamiento, y estado de la pared base.'
    },
    {
      id: 'acustico',
      title: 'Aislamiento Acústico e Insonorización',
      icon: 'ri-volume-mute-line',
      description: 'Soluciones específicas para reducir ruido y mejorar confort',
      image: 'https://readdy.ai/api/search-image?query=acoustic%20insulation%20soundproofing%20mineral%20wool%20installation%20interior%20wall%20construction%20noise%20reduction&width=600&height=800&seq=service-acustico-008&orientation=portrait',
      includes: [
        'Tabiques dobles con lana de roca',
        'Trasdosados acústicos',
        'Techos flotantes',
        'Bandas resilientes antivibratorias',
        'Sellado acústico de juntas',
        'Asesoramiento técnico especializado'
      ],
      idealFor: [
        'Viviendas con ruido de vecinos',
        'Estudios de música o grabación',
        'Salas de cine en casa',
        'Oficinas que requieren privacidad',
        'Locales comerciales ruidosos'
      ],
      priceFactors: 'El coste varía según el nivel de aislamiento requerido (dB), superficie a tratar, tipo de solución (tabique, trasdosado, techo) y materiales específicos.'
    },
    {
      id: 'termico',
      title: 'Aislamiento Térmico',
      icon: 'ri-temp-cold-line',
      description: 'Mejora la eficiencia energética y el confort de tu hogar',
      image: 'https://readdy.ai/api/search-image?query=thermal%20insulation%20installation%20energy%20efficiency%20wall%20insulation%20foam%20board%20mineral%20wool%20interior%20construction&width=600&height=800&seq=service-termico-009&orientation=portrait',
      includes: [
        'Trasdosados con aislamiento térmico',
        'Lana mineral de alta densidad',
        'Paneles de poliestireno extruido (XPS)',
        'Corrección de puentes térmicos',
        'Mejora de certificación energética',
        'Reducción de condensaciones'
      ],
      idealFor: [
        'Viviendas con paredes frías',
        'Reducir factura de calefacción',
        'Eliminar condensaciones y humedad',
        'Mejorar confort térmico',
        'Aumentar valor de la vivienda'
      ],
      priceFactors: 'El precio depende de los metros cuadrados, tipo y grosor de aislamiento, si incluye trasdosado completo y estado de las paredes existentes.'
    },
    {
      id: 'antihumedad',
      title: 'Pladur Antihumedad',
      icon: 'ri-drop-line',
      description: 'Placas especiales para zonas húmedas como baños y cocinas',
      image: 'https://readdy.ai/api/search-image?query=green%20moisture%20resistant%20drywall%20bathroom%20installation%20water%20resistant%20plasterboard%20wet%20area%20construction&width=600&height=800&seq=service-antihumedad-010&orientation=portrait',
      includes: [
        'Placas de pladur WA (color verde)',
        'Resistencia a la humedad',
        'Tratamiento hidrófugo',
        'Instalación en baños completos',
        'Instalación en cocinas y lavaderos',
        'Base perfecta para alicatado'
      ],
      idealFor: [
        'Baños y aseos',
        'Cocinas',
        'Lavaderos y zonas de lavado',
        'Sótanos con humedad',
        'Cualquier zona con vapor de agua'
      ],
      priceFactors: 'El coste es similar al pladur estándar con un pequeño incremento por el material especial. Depende de metros cuadrados y complejidad de la instalación.'
    },
    {
      id: 'reformas',
      title: 'Reformas y Redistribución',
      icon: 'ri-home-gear-line',
      description: 'Proyectos completos de reforma con pladur para viviendas, oficinas y locales',
      image: 'https://readdy.ai/api/search-image?query=complete%20interior%20renovation%20drywall%20construction%20modern%20apartment%20office%20reform%20professional%20team%20workspace&width=600&height=800&seq=service-reformas-011&orientation=portrait',
      includes: [
        'Proyecto de redistribución completo',
        'Demolición de tabiques existentes',
        'Nuevos tabiques de pladur',
        'Falsos techos',
        'Trasdosados y aislamientos',
        'Coordinación con otros gremios'
      ],
      idealFor: [
        'Reformas integrales de viviendas',
        'Redistribución de oficinas',
        'Acondicionamiento de locales',
        'Cambio de distribución completo',
        'Modernización de espacios'
      ],
      priceFactors: 'El precio depende del alcance del proyecto, metros cuadrados totales, complejidad de la redistribución, nivel de acabados y coordinación con otros trabajos.'
    }
  ];

  const scrollToService = (serviceId: string) => {
    setActiveService(serviceId);
    const element = document.getElementById(serviceId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <>
      <Header />
      <WhatsAppButton />

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-primary/10 to-primary-light/10 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20drywall%20construction%20tools%20materials%20modern%20workspace%20clean%20organized%20plasterboard%20installation&width=1920&height=600&seq=hero-servicios-001&orientation=landscape"
            alt="Servicios de Pladur"
            className="w-full h-full object-cover object-center"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/80 to-white/85"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <nav className="text-sm text-gray-600 mb-6">
              <Link to="/" className="hover:text-primary cursor-pointer">Inicio</Link>
              <span className="mx-2">&gt;</span>
              <span className="text-primary font-semibold">Servicios</span>
            </nav>
            <h1 className="text-5xl md:text-6xl font-bold text-text-dark mb-6">
              Servicios de <strong>Pladur</strong> en L'Hospitalet de Llobregat
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Soluciones completas de instalación de pladur para todo tipo de proyectos. <Link to="/empresa" className="text-primary hover:text-primary-dark font-semibold transition-colors">Conoce nuestra empresa</Link>.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Navegación Lateral + Contenido */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar - Menú de Servicios */}
            <aside className="lg:w-1/4">
              <div className="lg:sticky lg:top-24 bg-bg-light rounded-2xl p-6">
                <h2 className="text-xl font-bold text-text-dark mb-4">Servicios</h2>
                <nav className="space-y-2">
                  {services.map((service) => (
                    <button
                      key={service.id}
                      onClick={() => scrollToService(service.id)}
                      className={`w-full text-left px-4 py-3 rounded-lg transition-all duration-300 flex items-center cursor-pointer ${
                        activeService === service.id
                          ? 'bg-primary text-white font-semibold'
                          : 'text-gray-700 hover:bg-white hover:text-primary'
                      }`}
                    >
                      <i className={`${service.icon} mr-3 text-lg`}></i>
                      <span className="text-sm">{service.title}</span>
                    </button>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Contenido Principal */}
            <div className="lg:w-3/4 space-y-16">
              {services.map((service, index) => (
                <motion.div
                  key={service.id}
                  id={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className="scroll-mt-24"
                >
                  <div className={`grid grid-cols-1 ${index % 2 === 0 ? 'lg:grid-cols-5' : 'lg:grid-cols-5'} gap-8 items-start`}>
                    {/* Contenido */}
                    <div className={`${index % 2 === 0 ? 'lg:col-span-3' : 'lg:col-span-3 lg:order-2'}`}>
                      <div className="flex items-center mb-4">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mr-4">
                          <i className={`${service.icon} text-2xl text-primary`}></i>
                        </div>
                        <h3 className="text-3xl md:text-4xl font-bold text-text-dark">{service.title}</h3>
                      </div>
                      
                      <p className="text-lg text-gray-600 mb-6 leading-relaxed">{service.description}</p>

                      <div className="mb-6">
                        <h4 className="text-xl font-bold text-text-dark mb-3">¿Qué incluye?</h4>
                        <ul className="space-y-2">
                          {service.includes.map((item, idx) => (
                            <li key={idx} className="flex items-start text-gray-600">
                              <i className="ri-check-line text-primary mr-2 mt-1 flex-shrink-0"></i>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mb-6">
                        <h4 className="text-xl font-bold text-text-dark mb-3">Ideal para:</h4>
                        <ul className="space-y-2">
                          {service.idealFor.map((item, idx) => (
                            <li key={idx} className="flex items-start text-gray-600">
                              <i className="ri-arrow-right-s-line text-primary mr-2 mt-1 flex-shrink-0"></i>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-primary/5 border-l-4 border-primary rounded-lg p-4 mb-6">
                        <p className="text-sm text-gray-700">
                          <strong className="text-text-dark">Precio:</strong> {service.priceFactors}
                        </p>
                      </div>

                      <a
                        href="https://wa.me/34629501941"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-6 py-3 bg-primary text-white font-semibold rounded-lg hover:bg-primary-dark transition-all duration-300 shadow-md hover:shadow-lg whitespace-nowrap cursor-pointer"
                      >
                        <i className="ri-whatsapp-line mr-2"></i>
                        Consultar para este servicio
                      </a>
                    </div>

                    {/* Imagen */}
                    <div className={`${index % 2 === 0 ? 'lg:col-span-2' : 'lg:col-span-2 lg:order-1'}`}>
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-auto rounded-2xl shadow-lg object-cover"
                        style={{ width: '600px', height: '800px' }}
                      />
                    </div>
                  </div>

                  {index < services.length - 1 && (
                    <div className="mt-16 border-b border-gray-200"></div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-20 bg-primary-dark">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              ¿Necesitas un presupuesto personalizado?
            </h2>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Contacta con nosotros y te asesoramos sobre la mejor solución de pladur para tu proyecto en L'Hospitalet de Llobregat. Consulta también nuestras <Link to="/faq" className="text-white underline hover:text-white/80 transition-colors">preguntas frecuentes</Link>.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+34629501941"
                className="inline-flex items-center justify-center px-8 py-4 bg-white text-primary font-bold text-lg rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-3 text-xl"></i>
                Llamar +34 629 501 941
              </a>
              <Link
                to="/contacto"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-bold text-lg rounded-lg hover:bg-white hover:text-primary transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-mail-line mr-3 text-xl"></i>
                Formulario de Contacto
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />

      <CookieBanner />

      {/* JSON-LD Schema - Service */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "itemListElement": services.map((service, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "Service",
              "name": service.title,
              "description": service.description,
              "provider": {
                "@type": "LocalBusiness",
                "name": "PLADUREX"
              },
              "areaServed": {
                "@type": "City",
                "name": "L'Hospitalet de Llobregat"
              }
            }
          }))
        })}
      </script>
    </>
  );
};

export default ServiciosPage;
